'use client'
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqData = [
  {
    id: "01",
    question: "What makes this B.Sc. in Hospitality & Hotel Management unique?",
    answer:
      "A fully UGC-recognised, three-year undergraduate degree awarded by Medhavi Skills University — a government-notified Skills University established under the MSU Act 2021. The programme follows a Work-Integrated Learning model where partner 5-star hotel properties serve as the campus. Students receive structured academic instruction alongside continuous on-the-job learning from Semester 1 itself. The degree is aligned with NEP 2020 and the UGC CCFUP, and integrates the Academic Bank of Credits (ABC) — ensuring full academic credibility alongside real industry experience.",
  },
  {
    id: "02",
    question: "What is the eligibility criteria for admission?",
    answer: "Candidates who have completed 10+2 (any stream) from a recognized board, with English as a subject. Candidates must be 18 years of age or above as on September 2026. All applicants undergo a pre-screening and selection interview prior to enrolment to ensure alignment with the programme and a hospitality career pathway.",
  },
  {
    id: "03",
    question: "How is the three-year programme structured?",
    answer: "The programme spans 6 semesters across three progressive years. Year 1 – Foundations & Operations: Begins with three months of intensive campus-based training covering grooming, communication, and hospitality essentials, followed by placement at a partner hotel with continuous academic delivery and support. Year 2 – Advanced Skills: Focuses on supervisory capabilities across Bar & Beverage, Rooms Division, HR Management, Digital Marketing, and Leadership Communication. Year 3 – Leadership & Management: Covers Revenue Management, Event Management, Entrepreneurship, and an Industry Capstone Project. Each semester is mapped to a progressive hotel job role — academic growth and professional development advance simultaneously.",
  },
  {
    id: "04",
    question: "What is the ratio of practical training to academic instruction?",
    answer: "The programme is structured as 70% practical training and 30% theory. Practical learning takes place through continuous On-the-Job Learning (OJL) in partner hotels, supervised by industry mentors. Academic sessions are conducted by MSU faculty at the hotel premises. All practical components are credit-aligned, competency-mapped, and jointly evaluated by MSU faculty and industry mentors. This is a degree-first, career-integrated pathway — every hour in the hotel is academically governed, not an informal training arrangement.",
  },
  {
    id: "05",
    question: "How are students assessed throughout the programme?",
    answer: "Assessments are multi-layered, covering both academic and practical competence. Academic evaluation comprises continuous assessment tools — quizzes, reflection writing, role plays, and simulations — along with a mid-term and end-term examination each semester. Practical assessment is conducted through a structured feedback mechanism where industry mentors evaluate each student's performance, professional conduct, and skill development against defined competency parameters.",
  },
  {
    id: "06",
    question: "Does the programme offer multiple exit options?",
    answer: "Yes, in line with the NEP 2020 framework: After Year 1 — UG Certificate in Hospitality & Hotel Management (NCrF Level 4.5). After Year 2 — UG Diploma in Hospitality & Hotel Management (NCrF Level 5). After Year 3 — B.Sc. Degree in Hospitality & Hotel Management (NCrF Level 5.5). All credits are registered under the Academic Bank of Credits (ABC), allowing students to re-enter and continue the programme at a later stage without losing prior academic progress.",
  },
  {
    id: "07",
    question: "What does 'industry-sponsored degree' mean for a student?",
    answer: "This programme is designed to be sponsored by the industry through the stipend earned by students during their On-the-Job Learning at partner hotels. Students receive a monthly stipend which is structured to cover their semester fee obligations across the three years of the programme, enabling students to complete a UGC-recognised degree at no net cost to themselves and rather walk away with savings."
  },
  {
    id: "08",
    question: "Which hotel properties will students be placed in?",
    answer:"Students are placed in 5-star properties from MSU's network of partner luxury hotel brands. Roseate Hotels & Resorts and Leela Palaces, Hotels and Resorts are the anchor properties for Delhi/NCR, while JW Marriott and Westin are the anchor properties for the Kolkata location."   
  },
  {
    id: "09",
    question: "What placement support is provided upon programme completion?",
    answer: "100% placement support is provided to all graduating students through two structured pathways: 1. Absorption by the anchor hotel — the training property holds first right of hiring and may offer a confirmed full-time role with a defined career trajectory. 2. Placement through MSU's network — students are placed across MSU's 30+ partner hotel brands in India and internationally. Graduates enter with three years of documented hotel experience, enabling direct entry into supervisory and mid-level roles — a distinct advantage over graduates of conventional hospitality programmes.",
  },
  {
    id: "10",
    question: "What student support systems are in place during the programme?",
    answer: "Every student is assigned a dedicated faculty mentor who provides ongoing academic guidance and personal support throughout all three years. The University operates a structured grievance redressal mechanism with a resolution commitment of 24 to 48 hours for any query or concern raised by a student or parent. MSU's academic and student support teams remain actively engaged throughout the 33-month hotel learning phase — ensuring continuity of mentorship, monitoring, and care.",
  },
];

export default function NextTailwindFAQ({ title, subtitle }) {
  const [openItem, setOpenItem] = useState("01");
  const [windowWidth, setWindowWidth] = useState(0);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }

    const handleResize = () => {
      if (typeof window !== "undefined") {
        setWindowWidth(window.innerWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? "" : id);
  };

  return (
    <div className="w-full py-8  px-3 xs:py-10 xs:px-4 sm:py-12 sm:px-6 md:py-16 lg:py-22 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="container 2xl:max-w-7xl xl:max-w-6xl lg:max-w-5xl  mx-auto">

          <div className="container mx-auto text-center mb-4 px-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-black leading-tight mb-4">{title}</h2>
            <p className="text-[#3d677e] mx-auto leading-tight">
              {subtitle}
            </p>
          </div>

          <div className="space-y-3 xs:space-y-4 sm:space-y-5 md:space-y-6 mt-16" >
            {faqData.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <div
                  className={`rounded-xl xs:rounded-2xl border transition-all duration-500 ease-out
                    ${openItem === item.id
                      ? "bg-white border-blue-200/50 shadow-2xl shadow-blue-500/10 scale-[1.01] xs:scale-[1.02] ring-1 ring-blue-100/50"
                      : "bg-white border-gray-200/40 shadow-lg shadow-gray-500/5 hover:bg-white hover:border-blue-200/30 hover:shadow-xl hover:shadow-blue-500/8 hover:scale-[1.005] xs:hover:scale-[1.01] hover:ring-1 hover:ring-blue-100/30"
                    }
                  `}
                >

                  <div
                    className="flex items-center justify-between p-4 xs:p-5 sm:p-6 md:p-8 cursor-pointer group"
                    onClick={() => toggleItem(item.id)}
                  >
                    <div className="flex items-center grow min-w-0 mr-2 xs:mr-3 sm:mr-4">

                      <div
                        className={`
                          text-lg xs:text-xl sm:text-2xl md:text-3xl lg:text-4xl 
                          min-w-8 xs:min-w-10 sm:min-w-12 md:min-w-16 
                          mr-2 xs:mr-3 sm:mr-4 md:mr-6 
                          transition-all duration-300 ease-in-out shrink-0
                          ${openItem === item.id ? "text-[#0475B3] drop-shadow-sm" : "text-slate-400 group-hover:text-[#0475B3]"}
                        `}
                      >
                        {item.id}
                      </div>

                      <h3
                        className={`
                          text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl 
                          leading-snug xs:leading-snug sm:leading-snug md:leading-snug lg:leading-snug 
                          mb-0 transition-all duration-300 ease-in-out
                          ${openItem === item.id ? "text-[#0475B3] drop-shadow-sm" : "text-slate-800 group-hover:text-[#0475B3]"}
                        `}
                      >
                        {item.question}
                      </h3>
                    </div>

                    <motion.button
                      className={`
                        w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 
                        rounded-full border-none transition-all duration-300 ease-in-out
                        flex items-center justify-center shrink-0
                        ${openItem === item.id
                          ? "bg-[#0475B3] text-white shadow-lg shadow-blue-600/25 ring-1 xs:ring-2 ring-blue-200/50"
                          : "bg-white text-slate-400 shadow-md shadow-gray-500/10 hover:bg-[#0475B3] hover:text-white hover:shadow-lg hover:shadow-[#0475B3] hover:ring-1 xs:hover:ring-2 hover:ring-[#0475B3] ring-1 ring-gray-200/30"
                        }
                      `}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <AnimatePresence mode="wait">
                        {openItem === item.id ? (
                          <motion.div
                            key="minus"
                            initial={{ opacity: 0, rotate: -90 }}
                            animate={{ opacity: 1, rotate: 0 }}
                            exit={{ opacity: 0, rotate: 90 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Minus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                          </motion.div>
                        ) : (
                          <motion.div
                            key="plus"
                            initial={{ opacity: 0, rotate: 90 }}
                            animate={{ opacity: 1, rotate: 0 }}
                            exit={{ opacity: 0, rotate: -90 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Plus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.button>
                  </div>

                  <AnimatePresence>
                    {openItem === item.id && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: "easeInOut" }}
                        style={{ overflow: "hidden" }}
                      >
                        <div className="px-4 xs:px-5 sm:px-6 md:px-8 pb-4 xs:pb-5 sm:pb-6 md:pb-8">
                          <div
                            className="border-l-3 xs:border-l-4 border-[#0475B3] rounded-r-lg xs:rounded-r-xl p-3 xs:p-4 sm:p-4 md:p-6 ml-8 xs:ml-10 sm:ml-12 md:ml-16 shadow-inner bg-blue-50/60"
                          >
                            <motion.p
                              className="mb-0 text-slate-700 text-sm xs:text-base sm:text-base md:text-lg leading-relaxed"
                              initial={{ y: -10, opacity: 0 }}
                              animate={{ y: 0, opacity: 1 }}
                              transition={{ duration: 0.3, delay: 0.1 }}
                            >
                              {item.answer}
                            </motion.p>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
